"use client";

import { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/utils";

export type ButtonVariant = "primary" | "secondary" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-gradient-to-r from-[#7A56E8] to-[#9B82FF] text-white hover:from-[#6D46D1] hover:to-[#8B6FE8] focus:ring-[#7A56E8] shadow-[0_4px_12px_rgba(122,86,232,0.22)]",
  secondary: "bg-white text-[#7A56E8] border-2 border-[#7A56E8] hover:bg-[#F5F3FF] focus:ring-[#7A56E8]",
  outline: "bg-white text-gray-700 border border-[#D9DCE5] hover:bg-gray-50 focus:ring-gray-500",
  ghost: "bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500",
  danger: "bg-[#EF4444] text-white hover:bg-[#DC2626] focus:ring-[#EF4444] shadow-[0_2px_8px_rgba(239,68,68,0.2)]",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-10 px-4 text-[14px] font-semibold rounded-[12px]",
  md: "h-12 px-5 text-[15px] font-bold rounded-[14px] min-h-[48px]",
  lg: "h-[52px] px-6 text-[16px] font-bold rounded-[14px] min-h-[52px]",
  icon: "h-12 w-12 rounded-[12px] min-h-[48px] min-w-[48px]",
};

export function Button({
  className,
  variant = "primary",
  size = "md",
  children,
  loading,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center font-medium transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        "disabled:opacity-50 disabled:pointer-events-none",
        "touch-manipulation active:scale-[0.98]",
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
}
